import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faComments, 
  faArrowTrendUp, 
  faArrowTrendDown,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import llmAxiosInstance from 'helpers/llmApi';
import { user_info } from 'jotai/UserJotaiInstance';
import { UserInfo } from 'interfaces/user';
import KairosLogo from 'assets/img/logos/KairosIQ-LogoOnly.png';
import { AIChatTriggerEvent } from 'utils/aiChatTrigger';
import './GlobalDashboardAI.css';

// TypeScript interfaces for AI chat
interface ChatMessage {
  type: 'user' | 'ai';
  message: string;
  timestamp: string;
  isTyping?: boolean;
  isError?: boolean;
  context?: any;
}

interface GlobalDashboardAIProps {
  isVisible?: boolean;
  excludedPaths?: string[];
}

const GlobalDashboardAI: React.FC<GlobalDashboardAIProps> = ({ 
  isVisible = true, // ENABLED: Restored with proper path exclusions
  excludedPaths = ['/leads', '/deals'] // Fixed: improved logic now handles /leads/123 correctly
}) => {
  const location = useLocation();
  const [userInfo] = useAtom<UserInfo>(user_info);
  
  // AI Chatbot state
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [chatbotMinimized, setChatbotMinimized] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Check if current path should show the assistant
  const shouldShowAssistant = useCallback(() => {
    if (!isVisible) return false;
    
    // Check if current path is in excluded paths
    const currentPath = location.pathname;
    console.log('🔍 [GLOBAL AI] Current path:', currentPath, 'Excluded paths:', excludedPaths);
    
    const isExcluded = excludedPaths.some(excludedPath => {
      // Handle both exact matches and prefix matches
      if (excludedPath.endsWith('/')) {
        // For paths ending with /, check if current path starts with it
        const shouldExclude = currentPath.startsWith(excludedPath);
        console.log('🔍 [GLOBAL AI] Checking prefix:', currentPath, 'starts with', excludedPath, '=', shouldExclude);
        return shouldExclude;
      } else {
        // For paths without /, check both exact match AND prefix match with added /
        const exactMatch = currentPath === excludedPath;
        const prefixMatch = currentPath.startsWith(excludedPath + '/');
        const shouldExclude = exactMatch || prefixMatch;
        console.log('🔍 [GLOBAL AI] Checking exact/prefix:', currentPath, '===', excludedPath, 'or starts with', excludedPath + '/', '=', shouldExclude);
        return shouldExclude;
      }
    });
    
    console.log('🔍 [GLOBAL AI] Should show assistant:', !isExcluded);
    return !isExcluded;
  }, [isVisible, location.pathname, excludedPaths]);

  // Get personalized greeting based on current page
  const getContextualGreeting = useCallback(() => {
    const firstName = userInfo?.user_first_name?.split(' ')[0] || userInfo?.user_email?.split('@')[0] || 'there';
    const currentPath = location.pathname;
    
    const contextualGreetings: { [key: string]: string } = {
      '/dashboard': `Hi ${firstName}! I'm your KairosIQ AI assistant. I can help with lead management, market insights, and workflow automation.`,
      '/analytics': `Welcome to Analytics, ${firstName}! I can help interpret your performance data and suggest optimization strategies.`,
      '/market-intelligence': `Hi ${firstName}! I can help you understand market trends and provide insights for your target areas.`,
      '/smart-cma': `Hello ${firstName}! I can assist with your CMA analysis and provide market context for your valuations.`,
      '/reports': `Hi ${firstName}! I can help you understand your reports and suggest actions based on the data.`
    };

    return contextualGreetings[currentPath] || 
           `Hi ${firstName}! I'm your KairosIQ AI assistant. I can help you with business insights and workflow optimization. What would you like to work on?`;
  }, [userInfo, location.pathname]);

  // Initialize chat with contextual greeting when component mounts or path changes
  useEffect(() => {
    if (shouldShowAssistant() && chatMessages.length === 0) {
      setChatMessages([{
        type: 'ai',
        message: getContextualGreeting(),
        timestamp: new Date().toISOString()
      }]);
    }
  }, [location.pathname, userInfo, chatMessages.length, shouldShowAssistant, getContextualGreeting]);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // Function to handle external AI chat triggers
  const triggerAIChat = useCallback(async (prompt: string, context?: any, source?: string) => {
    console.log(`🤖 [GLOBAL AI] Received trigger from ${source}:`, prompt);
    
    // Open the chatbot if it's not already open
    if (!chatbotOpen) {
      setChatbotOpen(true);
    }
    
    // Ensure chatbot is not minimized
    if (chatbotMinimized) {
      setChatbotMinimized(false);
    }
    
    // Add the user message directly to chat
    const userMessage: ChatMessage = {
      type: 'user',
      message: prompt,
      timestamp: new Date().toISOString()
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    // Add typing indicator
    const typingMessage: ChatMessage = {
      type: 'ai',
      message: 'KairosIQ AI is analyzing your request...',
      timestamp: new Date().toISOString(),
      isTyping: true
    };
    setChatMessages(prev => [...prev, typingMessage]);
    
    try {
      // Get conversation history for context
      const conversationHistory = chatMessages
        .filter(msg => !msg.isTyping && !msg.isError)
        .slice(-6) // Keep last 6 messages for context
        .map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.message
        }));

      // Add current page context
      const pageContext = {
        currentPage: location.pathname,
        pageName: getPageDisplayName(location.pathname),
        timestamp: new Date().toISOString(),
        triggerSource: source,
        triggerContext: context
      };

      // Generate conversation ID if not exists
      const conversationId = sessionStorage.getItem('ai_conversation_id') || `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('ai_conversation_id', conversationId);

      // Call the existing Python AI server general chat endpoint
      const response = await llmAxiosInstance.post('/chat/internal/general', {
        message: prompt,
        user_id: userInfo?.id,
        conversation_id: conversationId,
        context: {
          conversation_history: conversationHistory,
          current_view: 'dashboard',
          page_context: pageContext,
          dashboard_context: pageContext
        }
      });

      const aiResponse = response.data?.response || "I'm here to help with your real estate business!";
      
      // Remove typing indicator and add real AI response
      setChatMessages(prev => {
        const filtered = prev.filter(msg => !msg.isTyping);
        return [...filtered, {
          type: 'ai',
          message: aiResponse,
          timestamp: new Date().toISOString(),
          context: response.data?.context || {}
        }];
      });
      
    } catch (error: any) {
      console.error('❌ AI Chat Trigger Error:', error);
      
      // Remove typing indicator and add error message
      setChatMessages(prev => {
        const filtered = prev.filter(msg => !msg.isTyping);
        return [...filtered, {
          type: 'ai',
          message: "I apologize, but I'm having trouble connecting right now. Please try again in a moment.",
          timestamp: new Date().toISOString(),
          isError: true
        }];
      });
    } finally {
      setIsLoading(false);
    }
  }, [chatbotOpen, chatbotMinimized, location.pathname, userInfo?.id]);

  // Listen for external AI chat triggers
  useEffect(() => {
    const handleAIChatTrigger = (event: CustomEvent<AIChatTriggerEvent>) => {
      const { prompt, context, source } = event.detail;
      
      console.log(`🤖 [GLOBAL AI] Received trigger from ${source}:`, prompt);
      
      // Open the chatbot if it's not already open
      if (!chatbotOpen) {
        setChatbotOpen(true);
      }
      
      // Ensure chatbot is not minimized
      if (chatbotMinimized) {
        setChatbotMinimized(false);
      }
      
      // Add the user message directly to chat and process
      const userMessage: ChatMessage = {
        type: 'user',
        message: prompt,
        timestamp: new Date().toISOString()
      };
      
      setChatMessages(prev => [...prev, userMessage]);
      setIsLoading(true);
      
      // Add typing indicator
      const typingMessage: ChatMessage = {
        type: 'ai',
        message: 'KairosIQ AI is analyzing your request...',
        timestamp: new Date().toISOString(),
        isTyping: true
      };
      setChatMessages(prev => [...prev, typingMessage]);
      
      // Process the AI request (simplified version)
      setTimeout(async () => {
        try {
          // Generate conversation ID if not exists
          const conversationId = sessionStorage.getItem('ai_conversation_id') || `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          sessionStorage.setItem('ai_conversation_id', conversationId);

          const response = await llmAxiosInstance.post('/chat/internal/general', {
            message: prompt,
            user_id: userInfo?.id,
            conversation_id: conversationId,
            context: {
              conversation_history: [],
              current_view: 'dashboard',
              page_context: {
                currentPage: location.pathname,
                pageName: 'Dashboard',
                timestamp: new Date().toISOString(),
                triggerSource: source,
                triggerContext: context
              }
            }
          }, {
            headers: {
              'Content-Type': 'application/json'
            }
          });

          const aiResponse = response.data?.response || "I'm here to help with your real estate business!";
          
          // Remove typing indicator and add real AI response
          setChatMessages(prev => {
            const filtered = prev.filter(msg => !msg.isTyping);
            return [...filtered, {
              type: 'ai',
              message: aiResponse,
              timestamp: new Date().toISOString(),
              context: response.data?.context || {}
            }];
          });
          
        } catch (error: any) {
          console.error('❌ AI Chat Trigger Error:', error);
          
          // Remove typing indicator and add error message
          setChatMessages(prev => {
            const filtered = prev.filter(msg => !msg.isTyping);
            return [...filtered, {
              type: 'ai',
              message: "I apologize, but I'm having trouble connecting right now. Please try again in a moment.",
              timestamp: new Date().toISOString(),
              isError: true
            }];
          });
        } finally {
          setIsLoading(false);
        }
      }, 100);
    };
    
    window.addEventListener('ai-chat-trigger', handleAIChatTrigger as EventListener);
    
    return () => {
      window.removeEventListener('ai-chat-trigger', handleAIChatTrigger as EventListener);
    };
  }, [chatbotOpen, chatbotMinimized, location.pathname, userInfo?.id]);

  // AI Chatbot handlers with real AWS Bedrock Claude integration
  const handleChatSubmit = async () => {
    if (!chatInput.trim() || isLoading) return;
    
    const userMessage: ChatMessage = {
      type: 'user',
      message: chatInput,
      timestamp: new Date().toISOString()
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    const currentInput = chatInput;
    setChatInput('');
    setIsLoading(true);
    
    // Add typing indicator
    const typingMessage: ChatMessage = {
      type: 'ai',
      message: 'KairosIQ AI is thinking...',
      timestamp: new Date().toISOString(),
      isTyping: true
    };
    setChatMessages(prev => [...prev, typingMessage]);
    
    try {
      // Get conversation history for context
      const conversationHistory = chatMessages
        .filter(msg => !msg.isTyping && !msg.isError)
        .slice(-6) // Keep last 6 messages for context
        .map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.message
        }));

      // Add current page context
      const pageContext = {
        currentPage: location.pathname,
        pageName: getPageDisplayName(location.pathname),
        timestamp: new Date().toISOString()
      };

      // Generate conversation ID if not exists
      const conversationId = sessionStorage.getItem('ai_conversation_id') || `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('ai_conversation_id', conversationId);

      // Call the existing Python AI server general chat endpoint
      const response = await llmAxiosInstance.post('/chat/internal/general', {
        message: currentInput,
        user_id: userInfo?.id,
        conversation_id: conversationId,
        context: {
          conversation_history: conversationHistory,
          current_view: 'dashboard',
          page_context: pageContext,
          dashboard_context: pageContext
        }
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const aiResponse = response.data?.response || "I'm here to help with your real estate business!";
      
      // Remove typing indicator and add real AI response
      setChatMessages(prev => {
        const filtered = prev.filter(msg => !msg.isTyping);
        return [...filtered, {
          type: 'ai',
          message: aiResponse,
          timestamp: new Date().toISOString(),
          context: response.data?.context || {}
        }];
      });
      
    } catch (error: any) {
      console.error('❌ AI Chat Error:', error);
      
      // Remove typing indicator and add error message
      setChatMessages(prev => {
        const filtered = prev.filter(msg => !msg.isTyping);
        return [...filtered, {
          type: 'ai',
          message: "I apologize, but I'm having trouble connecting right now. Please try again in a moment.",
          timestamp: new Date().toISOString(),
          isError: true
        }];
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get display name for current page
  const getPageDisplayName = (pathname: string): string => {
    const pathMap: { [key: string]: string } = {
      '/dashboard': 'Dashboard',
      '/analytics': 'Analytics',
      '/market-intelligence': 'Market Intelligence',
      '/smart-cma': 'Smart CMA',
      '/reports': 'Reports'
    };
    return pathMap[pathname] || 'KairosIQ';
  };

  // Handle suggestion clicks
  const handleSuggestion = (suggestion: string) => {
    setChatInput(suggestion);
    // The suggestion will be automatically submitted when user presses enter or clicks send
  };

  // Get contextual suggestions based on current page
  const getContextualSuggestions = () => {
    const currentPath = location.pathname;
    
    const suggestions: { [key: string]: string[] } = {
      '/dashboard': [
        "Show me my hottest leads",
        "What are my key performance metrics?",
        "Generate insights for my pipeline"
      ],
      '/analytics': [
        "Analyze my conversion trends",
        "What's my ROI this month?",
        "Compare my performance to last quarter"
      ],
      '/market-intelligence': [
        "Show me market trends for Scottsdale",
        "Which areas have the highest appreciation?",
        "Market insights for under $500k"
      ],
      '/smart-cma': [
        "Help me price this property",
        "Explain market conditions",
        "Find comparable properties"
      ],
      '/reports': [
        "Summarize my monthly performance",
        "What actions should I take?",
        "Export insights to PDF"
      ]
    };

    return suggestions[currentPath] || [
      "Show me my business overview",
      "What should I focus on today?",
      "Help me optimize my workflow"
    ];
  };

  // Don't render if not visible or on excluded paths
  if (!shouldShowAssistant()) {
    return null;
  }

  return (
    <div className={`global-ai-chatbot-container ${chatbotOpen ? 'open' : ''} ${chatbotMinimized ? 'minimized' : ''}`}>

      {!chatbotOpen && (
        <div className="global-chatbot-toggle" onClick={() => setChatbotOpen(true)}>
          <div className="chatbot-toggle-content">
            <FontAwesomeIcon icon={faComments} className="me-2" />
            <span>KairosIQ AI Assistant</span>
            <div className="ai-pulse-indicator"></div>
          </div>
        </div>
      )}
      
      {chatbotOpen && (
        <div className="global-chatbot-panel">
          <div className="chatbot-header">
            <div className="d-flex align-items-center">
              <div className="ai-avatar-small me-2">
                <img 
                  src={KairosLogo} 
                  alt="KairosIQ AI" 
                  style={{
                    width: '28px',
                    height: '28px',
                    objectFit: 'cover',
                    filter: 'brightness(1.1) contrast(1.2)',
                    backgroundColor: 'white',
                    padding: '2px',
                    borderRadius: '6px',
                    border: '1px solid rgba(0,0,0,0.1)'
                  }}
                />
              </div>
              <div>
                <h6 className="mb-0">KairosIQ AI Assistant</h6>
                <small className="text-muted">
                  {getPageDisplayName(location.pathname)} Intelligence
                </small>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button 
                variant="link" 
                size="sm" 
                className="p-0 text-muted"
                onClick={() => setChatbotMinimized(!chatbotMinimized)}
                title={chatbotMinimized ? "Expand" : "Minimize"}
              >
                <FontAwesomeIcon icon={chatbotMinimized ? faArrowTrendUp : faArrowTrendDown} />
              </Button>
              <Button 
                variant="link" 
                size="sm" 
                className="p-0 text-muted"
                onClick={() => setChatbotOpen(false)}
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          </div>
          
          {!chatbotMinimized && (
            <>
              <div className="chat-messages">
                {chatMessages.map((msg, index) => (
                  <div key={index} className={`message ${msg.type}`}>
                    {msg.type === 'ai' && (
                      <div className="ai-avatar-tiny me-2">
                        <img 
                          src={KairosLogo} 
                          alt="KairosIQ AI" 
                          style={{
                            width: '20px',
                            height: '20px',
                            objectFit: 'cover',
                            filter: 'brightness(1.1) contrast(1.2)',
                            backgroundColor: 'white',
                            padding: '1px',
                            borderRadius: '3px',
                            border: '1px solid rgba(0,0,0,0.1)'
                          }}
                        />
                      </div>
                    )}
                    <div className="message-content">
                      <p className="mb-1">{msg.message}</p>
                      <small className="timestamp">
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </small>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              
              <div className="chatbot-input">
                <div className="d-flex">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={`Ask about ${getPageDisplayName(location.pathname).toLowerCase()}...`}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleChatSubmit();
                      }
                    }}
                    disabled={isLoading}
                  />
                  <Button 
                    variant="primary" 
                    className="ms-2"
                    onClick={handleChatSubmit}
                    disabled={!chatInput.trim() || isLoading}
                  >
                    <FontAwesomeIcon icon={faComments} />
                  </Button>
                </div>
              </div>
              
              <div className="suggestion-chips">
                <div className="d-flex gap-2 flex-wrap">
                  {getContextualSuggestions().map((suggestion, index) => (
                    <button 
                      key={index}
                      className="suggestion-chip"
                      onClick={() => handleSuggestion(suggestion)}
                      disabled={isLoading}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalDashboardAI; 